import React, { useState, useRef, useEffect } from 'react';
import { Card, Input, Button, Select, Spin, Empty, message } from 'antd';
import { SendOutlined, TeamOutlined, ReloadOutlined } from '@ant-design/icons';
import { apiFetch } from '../utils/api.js';

// 员工头像配色
const MEMBER_STYLE = {
  '缇娜': { bg: '#FDEEE8', color: 'var(--brand)', border: '#F3D9CC', label: '缇' },
  '河伯': { bg: '#E8F1FB', color: '#1F5C99', border: '#BBD6F0', label: '河' },
  '小李': { bg: '#FBF3E4', color: '#8B6914', border: '#E8D9B8', label: '李' },
  '老板': { bg: 'var(--brand)', color: '#fff', border: 'var(--brand)', label: '总' },
};

function Avatar({ name, size = 34 }) {
  const s = MEMBER_STYLE[name] || MEMBER_STYLE['老板'];
  return (
    <span style={{
      width: size, height: size, borderRadius: 6, flexShrink: 0,
      background: s.bg, color: s.color, border: '1px solid ' + s.border,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size > 40 ? 18 : 13, fontWeight: 600,
    }}>{s.label}</span>
  );
}

// 时间戳格式化：当天显示 HH:mm，跨天显示 MM-DD HH:mm
function fmtTime(ts) {
  const s = String(ts || '');
  const today = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const todayStr = today.getFullYear() + '-' + pad(today.getMonth() + 1) + '-' + pad(today.getDate());
  const date = s.slice(0, 10);
  const hm = s.slice(11, 16) || s.slice(0, 16);
  return date === todayStr ? hm : s.slice(5, 16);
}

// 是否需要显示居中时间戳（跨天 / 间隔 >5 分钟）
function needTimeSep(ts, lastTs) {
  if (!lastTs) return true;
  const a = new Date(String(ts).replace(' ', 'T') + '+08:00');
  const b = new Date(String(lastTs).replace(' ', 'T') + '+08:00');
  if (isNaN(a) || isNaN(b)) return true;
  if (String(ts).slice(0, 10) !== String(lastTs).slice(0, 10)) return true;
  return a - b > 5 * 60 * 1000;
}

export default function TeamBoard() {
  const [messages, setMessages] = useState([]); // 正序：旧在上，新在下（最新在底部）
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [to, setTo] = useState('小李');
  const [content, setContent] = useState('');
  const boxRef = useRef(null);
  const autoScrollRef = useRef(true); // 用户是否在底部：在底部才自动滚，往上翻历史时不打扰

  const load = () => {
    setLoading(true);
    apiFetch('/api/team/messages').then(r => r.json()).then(d => {
      if (d.success) {
        // 后端返回「新在前」，反转为「旧在前」→ 最新消息沉底
        setMessages((d.data || []).slice().reverse());
      }
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // 轮询：AI 员工异步回复后自动出现在界面，无需切页重载（按 id 去重，只追加新消息）
  useEffect(() => {
    const timer = setInterval(() => {
      apiFetch('/api/team/messages?limit=100').then(r => r.json()).then(d => {
        if (d.success && Array.isArray(d.data)) {
          const reversed = d.data.slice().reverse(); // 旧在前
          setMessages(prev => {
            const prevIds = new Set(prev.map(m => m.id));
            const newOnes = reversed.filter(m => !prevIds.has(m.id));
            return newOnes.length ? [...prev, ...newOnes] : prev;
          });
        }
      }).catch(() => {});
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  // 消息变化时滚动到底部（仅在用户本来就在底部时）
  useEffect(() => {
    if (autoScrollRef.current && boxRef.current) {
      boxRef.current.scrollTop = boxRef.current.scrollHeight;
    }
  }, [messages]);

  const onScroll = () => {
    const el = boxRef.current;
    if (!el) return;
    autoScrollRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  };

  const send = async () => {
    const c = content.trim();
    if (!c || sending) return;
    setSending(true);
    try {
      const res = await apiFetch('/api/team/messages', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to, content: c }),
      }).then(r => r.json());
      if (res.success) {
        setContent('');
        // 立即把新消息追加到底部（不用重新拉全量，避免闪烁）
        if (res.message) setMessages(prev => [...prev, res.message]);
      } else {
        message.error(res.error || '发送失败');
      }
    } catch (e) { message.error('网络错误'); }
    setSending(false);
  };

  // 群成员头像组
  const memberStrip = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      {['缇娜', '河伯', '小李'].map(name => (
        <Avatar key={name} name={name} size={28} />
      ))}
    </div>
  );

  let lastTs = null;

  return (
    <div className="page-container">
      <Card
        title={
          <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <TeamOutlined style={{ color: 'var(--brand)', fontSize: 20 }} />
            <span>
              <span style={{ fontSize: 16, fontWeight: 500, color: '#26201C' }}>AI 团队</span>
              <span style={{ fontSize: 12, color: '#8c8c8c', marginLeft: 8 }}>群聊 · 缇娜 / 河伯 / 小李 / 你</span>
            </span>
          </span>
        }
        extra={
          <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {memberStrip}
            <Button size="small" icon={<ReloadOutlined />} onClick={load}>刷新</Button>
          </span>
        }
        style={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}
        bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}
      >
        {/* 消息流（微信聊天底色） */}
        <div ref={boxRef} onScroll={onScroll} style={{ flex: 1, overflowY: 'auto', minHeight: 0, background: '#F5F5F5', padding: '12px 16px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}><Spin /></div>
          ) : messages.length === 0 ? (
            <div style={{ padding: '40px 0' }}>
              <Empty description="还没有交流记录。在下面 @ 某位员工说句话，或去对话页让员工之间协作" />
            </div>
          ) : messages.map((m, i) => {
            const isMe = m.from === '老板';
            const s = MEMBER_STYLE[m.from] || MEMBER_STYLE['老板'];
            const showTime = needTimeSep(m.ts, lastTs);
            if (showTime) lastTs = m.ts;
            return (
              <div key={m.id || i}>
                {showTime && (
                  <div style={{ textAlign: 'center', margin: '10px 0 14px' }}>
                    <span style={{ fontSize: 11.5, color: '#b0b0b0', background: '#e8e8e8', borderRadius: 8, padding: '2px 10px' }}>{fmtTime(m.ts)}</span>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 10, marginBottom: 16, justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
                  {!isMe && <Avatar name={m.from} size={36} />}
                  <div style={{ maxWidth: '68%', display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3, flexDirection: isMe ? 'row-reverse' : 'row' }}>
                      <span style={{ fontWeight: 600, fontSize: 12.5, color: isMe ? '#26201C' : s.color }}>{isMe ? '我' : m.from}</span>
                      {m.to && m.to !== m.from && (
                        <span style={{ fontSize: 11, color: '#b0b0b0' }}>@{m.to === '老板' ? '我' : m.to}</span>
                      )}
                    </div>
                    <div style={{
                      background: isMe ? '#95EC69' : '#fff',
                      color: '#26201C',
                      borderRadius: 8,
                      borderTopRightRadius: isMe ? 2 : 8,
                      borderTopLeftRadius: isMe ? 8 : 2,
                      padding: '9px 13px', fontSize: 14, lineHeight: 1.6,
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                      border: isMe ? 'none' : '0.5px solid #ebebeb',
                    }}>{m.content}</div>
                  </div>
                  {isMe && <Avatar name="老板" size={36} />}
                </div>
              </div>
            );
          })}
        </div>

        {/* 底部输入区 */}
        <div style={{ display: 'flex', gap: 8, padding: '12px 16px', borderTop: '0.5px solid #e5e5e5', background: '#fff' }}>
          <Select
            value={to}
            onChange={setTo}
            style={{ width: 118 }}
            options={['缇娜', '河伯', '小李'].map(n => ({ value: n, label: '@ ' + n }))}
          />
          <Input
            placeholder="说点什么…（@ 提醒某位员工，会进入他的未读）"
            value={content}
            onChange={e => setContent(e.target.value)}
            onPressEnter={send}
            allowClear
          />
          <Button type="primary" icon={<SendOutlined />} loading={sending} onClick={send} style={{ background: '#95EC69', borderColor: '#95EC69', color: '#26201C' }}>发送</Button>
        </div>
      </Card>
    </div>
  );
}
